/*------------------------------------------------------------------------------
   About      : Tag / untag recommendations (epic DA-697, II-FE-4)

                Mockup: `image-2.png` — "Related recommendations", a search-and-
                tag picker over the currently-tagged list.

                ⚠️ **Tagging changes which QA transitions are legal.** The
                response carries a FRESH `allowedTransitions` for the insight,
                because the cross-entity gate (DA-2372) is evaluated per record
                and cannot be recomputed client-side. It is handed straight to
                the caller so the QA dropdown re-renders from it rather than
                keeping the pre-tag array.

                Tagging is an edit, so it obeys the same TODO-only rule as any
                other content change — reflected as a disabled state here rather
                than letting the call fail.

                Picker note: `/reco/getRecos` takes only `utilityDocumentId` —
                there is no `search` param — so the pilot's recos are fetched
                once and filtered locally.

                **Three modes.** With a saved `insight` each pick is written
                immediately through `tagRecommendations`. Without one — the
                create screen — picks are buffered in the caller's state and
                applied after the record exists, because the endpoint is keyed
                on `insightDocumentId` and `createInsight`'s payload has no slot
                for relations. Those two render the same; only where the list
                lives differs. `readOnly` is the third: the tagged list alone,
                for the Published tab, and it skips the fetch entirely.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Autocomplete, Box, Chip, CircularProgress, IconButton, Paper,
         Stack, TextField, Tooltip, Typography } from '@mui/material'
import DeleteOutlineIcon  from '@mui/icons-material/DeleteOutline'
import LightbulbIcon      from '@mui/icons-material/LightbulbOutlined'

import { useGlobalConfig }      from 'context/GlobalContext'
import { RunContextApp }        from 'framework/RCApp'
import { ANALYTICS_EVENTS }     from 'framework/analytics/AnalyticsEvents'
import { UI_PAGES }             from 'framework/access/UiRegistry'
import { PermissionGate }       from 'components/access/PermissionGate'
import { RecoApis }             from 'apis/recoAPI'
import { CONTENT_VERIFICATION_STATUS } from 'types/cms/frontendTypes'

import { InsightApis }          from '../api/insightAPI'
import { InsightRecord,
         TaggedRecommendation } from '../types/insightTypes'
import { FIELD_LABEL_SX,
         SECTION_HEADING_SX,
         SUB_HEADING_SX }       from './insightFormStyles'

interface TagRecommendationsPanelProps {
  utilityDocumentId  : string
  /** The saved record. Absent on create, which switches the panel to pending mode. */
  insight           ?: InsightRecord
  /** Receives the server's updated record — it carries fresh allowedTransitions.
   *  Saved mode only. */
  onTagged          ?: (updated: InsightRecord) => void
  /** Pending mode: the picks held by the caller until the record exists. */
  pendingTags       ?: TaggedRecommendation[]
  onPendingChange   ?: (next: TaggedRecommendation[]) => void
  disabled          ?: boolean
  /**
   * Reference mode — the tagged list only, no picker.
   *
   * Stronger than `disabled`: it also **skips the `/reco/getRecos` fetch
   * entirely**. That call exists to populate the picker, and the picker is not
   * rendered here, so the published tab would otherwise pay for the pilot's
   * whole reco list on every tab switch to show a list it already holds.
   */
  readOnly          ?: boolean
}

/**
 * Collapses concurrent fetches for the same pilot into one request.
 *
 * Two mounts of this panel ask for the same list at the same moment — React
 * StrictMode double-invokes effects in development, and the create and edit
 * screens both render the panel. Each mount issuing its own round trip for an
 * identical, unchanged list is pure waste.
 *
 * Deliberately caches only the **in-flight** promise, not the resolved value:
 * a session-long cache would hide a recommendation created since the page
 * loaded, and this list is the set of things an author may tag. The entry is
 * dropped the moment the request settles — success or failure — so the next
 * mount fetches fresh.
 */
const inFlightRecos = new Map<string, Promise<any>>()

function fetchRecosOnce(rc: RunContextApp, utilityDocumentId: string) {
  const existing = inFlightRecos.get(utilityDocumentId)
  if (existing) return existing

  const request = RecoApis.getRecosForUtility(rc, utilityDocumentId)
    .finally(() => { inFlightRecos.delete(utilityDocumentId) })

  inFlightRecos.set(utilityDocumentId, request)
  return request
}

interface RecoOption {
  documentId       : string
  recommendationId : string
  title            : string
}

export const TagRecommendationsPanel = ({
  utilityDocumentId,
  insight,
  onTagged,
  pendingTags,
  onPendingChange,
  disabled,
  readOnly,
}: TagRecommendationsPanelProps) => {

  const { rc } = useGlobalConfig()

  const [options, setOptions]     = useState<RecoOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving]   = useState(false)
  const [error, setError]         = useState<string | undefined>()

  const isPending = !insight

  const tagged = useMemo<TaggedRecommendation[]>(
    () => (insight ? insight.recommendations ?? [] : pendingTags ?? []),
    [insight, pendingTags],
  )

  // Tagging is an edit, so the same TODO-only rule applies as to any content
  // change. Disabling up front beats letting the server reject the call.
  // A record that does not exist yet has no status to be locked by.
  const lockedByStatus = !!insight && insight.verificationStatus !== CONTENT_VERIFICATION_STATUS.TODO
  const isDisabled     = disabled || isSaving || lockedByStatus

  /* Guards against an earlier pilot's slower response landing last and
     repopulating the picker with recos from a project the user has left. Same
     fix as `ConfigRegistryPage`'s requestSeqRef, and it matters more here: a
     stale option is not a stale row to look at, it is a reco the author can
     actually tag. */
  const requestSeqRef = useRef(0)

  const loadRecos = useCallback(async () => {
    const seq = ++requestSeqRef.current

    /* Drop the previous pilot's list up front rather than leaving it visible
       for the length of the round trip. quill scopes `/reco/getRecos` by
       `utility.documentId`, so the server would never *return* another pilot's
       reco — but it also has no reason to refuse a tag request naming one, and
       the only thing standing between the two is what this list holds. */
    setOptions([])
    setError(undefined)

    if (!utilityDocumentId) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)

    // No `search` param exists on this endpoint, so the whole pilot comes back
    // and Autocomplete filters locally.
    const resp = await fetchRecosOnce(rc, utilityDocumentId)

    // A superseded request must not touch state at all — not the options, not
    // the spinner, not the error.
    if (seq !== requestSeqRef.current) return

    setIsLoading(false)

    if (resp.error || !resp.data?.data) {
      // Options stay empty: an unavailable list must read as "nothing to offer",
      // never as the last pilot's list.
      setError('Could not load recommendations to tag.')
      return
    }

    // Recos are partitioned draft / modified / published exactly like insights,
    // and a record with an unpublished draft appears in more than one bucket.
    // Deduped on documentId — NOT on recommendationId, which is unique per pilot
    // rather than globally, the same caveat that applies to an insight's `code`.
    const { draft, modified, published } = resp.data.data
    const byDocumentId = new Map<string, RecoOption>()

    for (const row of [...(modified ?? []), ...(draft ?? []), ...(published ?? [])]) {
      const reco = row as unknown as {
        documentId ?: string
        recommendationId ?: string
        title ?: string
      }
      if (!reco?.documentId || byDocumentId.has(reco.documentId)) continue
      byDocumentId.set(reco.documentId, {
        documentId       : reco.documentId,
        recommendationId : reco.recommendationId ?? '',
        title            : reco.title || reco.recommendationId || reco.documentId,
      })
    }

    setOptions([...byDocumentId.values()]
      .sort((a, b) => a.title.localeCompare(b.title)))
  }, [rc, utilityDocumentId])

  // The fetch exists to fill the picker. No picker, no fetch.
  useEffect(() => { if (!readOnly) loadRecos() }, [loadRecos, readOnly])

  const applyTagging = async (
    recommendationDocumentIds : string[],
    mode                      : 'ADD' | 'REMOVE',
  ) => {
    if (!insight) return

    setIsSaving(true)
    setError(undefined)

    const resp = await InsightApis.tagRecommendations(rc, {
      insightDocumentId : insight.documentId,
      recommendationDocumentIds,
      mode,
    })

    setIsSaving(false)

    if (resp.error) {
      setError(resp.error.message || 'Tagging failed')
      return
    }
    if (resp.data?.error?.length) {
      setError(resp.data.error
        .flatMap((entry) => Object.entries(entry).map(([k, v]) => `${k}: ${v}`))
        .join('; '))
      return
    }

    const updated = resp.data?.insight
    if (updated) {
      // Straight through to the caller: this record's allowedTransitions have
      // changed, and only the server can compute them.
      onTagged?.(updated)
      rc.analytics.trackEvent(
        mode === 'ADD'
          ? ANALYTICS_EVENTS.NBI_INSIGHTS.RECO_TAGGED
          : ANALYTICS_EVENTS.NBI_INSIGHTS.RECO_UNTAGGED,
      )
    }
  }

  /* Pending mode keeps only what a TaggedRecommendation can hold from the
     picker. `editStatus` and `verificationStatus` are deliberately left unset
     rather than copied off the option: the server returns the authoritative
     values once the tags are applied, and a guessed badge here would contradict
     them for the rest of the session. */
  const addTag = (option: RecoOption) => {
    if (isPending) {
      onPendingChange?.([...tagged, {
        documentId       : option.documentId,
        recommendationId : option.recommendationId,
        title            : option.title,
      }])
      return
    }
    // ADD rather than SET: SET would silently drop every existing tag.
    applyTagging([option.documentId], 'ADD')
  }

  const removeTag = (documentId: string) => {
    if (isPending) {
      onPendingChange?.(tagged.filter((reco) => reco.documentId !== documentId))
      return
    }
    applyTagging([documentId], 'REMOVE')
  }

  const taggedIds = new Set(tagged.map((t) => t.documentId))
  const untagged  = options.filter((option) => !taggedIds.has(option.documentId))

  return (
    <Paper variant="outlined" sx={{ p : 2, mb : 2 }}>
      <Typography variant="subtitle2" sx={{ ...SECTION_HEADING_SX, mb : 1.5 }}>
        RELATED RECOMMENDATIONS ({tagged.length})
      </Typography>

      {/* All three of these explain why tagging is unavailable *right now*. In
          reference mode it is not unavailable, it is not offered — so they would
          read as a problem with a version that has none. */}
      {!readOnly && lockedByStatus && (
        <Alert severity="info" sx={{ mb : 1.5 }}>
          Tagging is a content edit, so it is only allowed while the QA status is
          To do. Move this insight back to To do to change its tags.
        </Alert>
      )}

      {!readOnly && isPending && (
        <Alert severity="info" sx={{ mb : 1.5 }}>
          These tags are applied immediately after the insight is created —
          tagging needs a saved record to attach to.
        </Alert>
      )}

      {!readOnly && error && <Alert severity="error" sx={{ mb : 1.5 }}>{error}</Alert>}

      {!readOnly && (
        <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.tag.id}>
          <Autocomplete
            size="small"
            options={untagged}
            loading={isLoading}
            disabled={isDisabled}
            value={null}
            blurOnSelect
            clearOnBlur
            getOptionLabel={(option) => `${option.title} (${option.recommendationId})`}
            onChange={(_event, option) => { if (option) addTag(option) }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tag recommendation"
                placeholder="Search and tag recommendations…"
                helperText="Only recommendations in this project can be tagged."
                sx={FIELD_LABEL_SX}
                InputProps={{
                  ...params.InputProps,
                  endAdornment : (
                    <>
                      {isLoading || isSaving ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </PermissionGate>
      )}

      {readOnly && !tagged.length && (
        <Typography variant="body2" color="text.secondary">
          No recommendations are tagged on the published version.
        </Typography>
      )}

      {!!tagged.length && (
        <>
          <Typography
            variant="caption"
            sx={{ ...SUB_HEADING_SX, mt : 2, mb : 0.5 }}
          >
            CURRENTLY TAGGED
          </Typography>

          <Stack spacing={1}>
            {tagged.map((reco) => (
              <Stack
                key={reco.documentId}
                direction="row" spacing={1} alignItems="center"
                sx={{ p : 1, border : '1px solid', borderColor : 'divider', borderRadius : 1 }}
              >
                <LightbulbIcon fontSize="small" sx={{ color : 'warning.main' }} />

                <Box sx={{ minWidth : 0, flexGrow : 1 }}>
                  <Typography variant="body2" sx={{ fontWeight : 600 }} noWrap>
                    {reco.title || reco.recommendationId}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily : 'monospace', color : 'text.secondary' }}>
                    {reco.recommendationId}
                  </Typography>
                </Box>

                {reco.disabled !== true && <Chip size="small" label="Active" color="success" variant="outlined" />}
                {reco.editStatus && <Chip size="small" label={reco.editStatus} />}

                {!readOnly && (
                  <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.tag.id}>
                    <Tooltip title="Untag">
                      <IconButton
                        size="small" color="error" disabled={isDisabled}
                        onClick={() => removeTag(reco.documentId)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </PermissionGate>
                )}
              </Stack>
            ))}
          </Stack>

          {/* Why an insight may be stuck: the gate is a monotonic floor, so a
              tagged reco that has never reached READY_TO_PUBLISH holds it back.
              The authoritative wording lives in blockedReason on the transition
              itself (DA-2392) — this is the orienting hint. Irrelevant on a
              version that has already cleared the gate. */}
          {!readOnly && (
            <Alert severity="info" sx={{ mt : 1.5 }} icon={false}>
              An insight can only reach Ready to publish once every tagged
              recommendation has reached it.
            </Alert>
          )}
        </>
      )}
    </Paper>
  )
}

export default TagRecommendationsPanel
